'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/navigation';
import {
  BookOpen,
  BarChart2,
  FlaskConical,
  ChevronLeft,
  ChevronRight,
  Settings,
  Bell,
  TrendingUp,
  LogOut,
  User,
  History,
  PlayCircle,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
}

const navItems: NavItem[] = [
  {
    id: 'nav-journal',
    label: 'Trading Journal',
    href: '/',
    icon: <BookOpen size={18} />,
  },
  {
    id: 'nav-learning',
    label: 'Learning Area',
    href: '/learning',
    icon: <TrendingUp size={18} />,
  },
  {
    id: 'nav-backtest',
    label: 'Auto-Backtest',
    href: '/auto-backtest-engine',
    icon: <FlaskConical size={18} />,
    badge: 2,
  },
  {
    id: 'nav-backtest-history',
    label: 'Backtest History',
    href: '/backtest-history',
    icon: <History size={18} />,
  },
  {
    id: 'nav-paper-trading',
    label: 'Paper Trading',
    href: '/paper-trading',
    icon: <PlayCircle size={18} />,
  },
  {
    id: 'nav-analytics',
    label: 'Analytics',
    href: '/analytics',
    icon: <BarChart2 size={18} />,
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();
  const { signOut, user } = useAuth();
  const router = useRouter();

  const handleLogout = async () => {
    try {
      await signOut();
      router.push('/sign-up-login');
      router.refresh();
    } catch {
      // ignore
    }
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col h-full border-r border-border bg-card sidebar-transition ${
          collapsed ? 'w-16' : 'w-60'
        } flex-shrink-0`}
      >
        {/* Logo */}
        <div className={`flex items-center border-b border-border h-16 px-4 ${collapsed ? 'justify-center' : 'gap-3'}`}>
          <AppLogo size={32} />
          {!collapsed && (
            <span className="font-bold text-base text-foreground tracking-tight">TradeEdge</span>
          )}
        </div>

        {/* Nav Items */}
        <nav className="flex-1 py-4 px-2 space-y-1">
          <div className={`mb-2 ${collapsed ? 'hidden' : 'block'}`}>
            <p className="text-xs font-500 text-muted-foreground uppercase tracking-widest px-3 mb-2">
              Main
            </p>
          </div>
          {navItems.map((item) => {
            const isActive = pathname === item.href;
            return (
              <Link
                key={item.id}
                href={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group relative ${
                  isActive
                    ? 'bg-primary/10 text-primary' :'text-foreground/70 hover:bg-muted hover:text-foreground'
                } ${collapsed ? 'justify-center' : ''}`}
                title={collapsed ? item.label : undefined}
              >
                <span className={`flex-shrink-0 ${isActive ? 'text-primary' : ''}`}>
                  {item.icon}
                </span>
                {!collapsed && (
                  <span className="flex-1 truncate">{item.label}</span>
                )}
                {!collapsed && item.badge && (
                  <span className="flex-shrink-0 bg-primary/20 text-primary text-xs font-600 px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                    {item.badge}
                  </span>
                )}
                {collapsed && item.badge && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom section */}
        <div className="border-t border-border p-2 space-y-1">
          <Link
            href="#"
            aria-label="Notifications"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? 'Notifications' : undefined}
          >
            <Bell size={18} />
            {!collapsed && <span>Notifications</span>}
          </Link>
          <Link
            href="/account"
            aria-label="Account"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? 'Account' : undefined}
          >
            <Settings size={18} />
            {!collapsed && <span>Account</span>}
          </Link>

          {/* User profile */}
          <div className={`flex items-center gap-3 px-3 py-2.5 mt-2 rounded-lg bg-muted/50 ${collapsed ? 'justify-center' : ''}`}>
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <User size={14} className="text-primary" />
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-xs font-600 text-foreground truncate">Marcus Chen</p>
                <p className="text-xs text-muted-foreground truncate">Pro Trader</p>
              </div>
            )}
            {!collapsed && (
              <button aria-label="Log out" onClick={handleLogout} className="text-muted-foreground hover:text-foreground transition-colors">
                <LogOut size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="absolute -right-3 top-20 w-6 h-6 bg-card border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary transition-all duration-150 z-10"
          style={{ position: 'sticky', bottom: '80px', marginLeft: collapsed ? '40px' : '220px' }}
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </aside>

      {/* Mobile: top bar placeholder — full mobile sidebar would be a drawer */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-card border-b border-border flex items-center px-4 gap-3">
        <AppLogo size={28} />
        <span className="font-bold text-sm text-foreground">TradeEdge</span>
        <div className="flex-1" />
        <nav className="flex items-center gap-1">
          {navItems.map((item) => {
            const isActive = pathname === item.href;
            return (
              <Link
                key={`mobile-${item.id}`}
                href={item.href}
                className={`p-2 rounded-lg transition-colors ${
                  isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground'
                }`}
              >
                {item.icon}
              </Link>
            );
          })}
        </nav>
      </div>
    </>
  );
}